import React, { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  TextField,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import DeleteOutlineTwoToneIcon from "@mui/icons-material/DeleteOutlineTwoTone";
import useFetchWithMsal from "../hooks/useFetchWithMsal";
import { protectedResources } from "../authConfig";
import Alert from "@mui/material/Alert";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AddTaskIcon from "@mui/icons-material/AddTask";
import { useTaskManager } from "./Obligo/useTaskManager"; // Import the custom hook for task management
import { isTestEnv } from "../globals";
import { useUser } from "../contexts/UserContext";
import { AddTaskForm } from "./Obligo/addTaskForm";

/**
 * Component for displaying and managing customer notes/tasks for an order
 * Allows users with proper permissions to add new tasks and append messages to existing ones
 * 
 * @param {Object} props
 * @param {Array} props.data - Array of customer notes/tasks
 * @param {Function} props.setOrders - Function to update orders state
 * @param {string} props.ordname - Order name/ID
 * @param {string} props.doerName - Optional doer name
 */
export const NotesAccordion = ({ data, setOrders, ordname, doerName }) => {
  const [expanded, setExpanded] = useState("panel0");
  const [newMessages, setNewMessages] = useState({});
  const [updateTasks, setUpdateTasks] = useState(data);
  const [isAddingTask, setIsAddingTask] = useState(false);
  const { newTask, setNewTask, loading, error, addTask, addMessage, resetTask } =
    useTaskManager();

  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });

  const { user } = useUser();

  /**
   * Handles accordion panel expansion/collapse
   */
  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  /**
   * Handles adding a new task to the order
   * Updates both local and parent state with the new task
   */
  const handleAddTask = async () => {
    const task = await addTask(ordname, doerName, (newTask) => {
      // Update local state
      setUpdateTasks((prevTasks) => [...prevTasks, newTask]);

      // Update parent state
      setOrders((prev) => {
        return prev.map((order) => {
          if (order.ORDNAME === ordname) {
            return {
              ...order,
              GENCUSTNOTES_SUBFORM: [...order.GENCUSTNOTES_SUBFORM, newTask],
            };
          }
          return order;
        });
      });
      // Close the form
      setIsAddingTask(false);
    });
  };

  /**
   * Cancels adding a new task and resets form
   */
  const handleCancelAddTask = () => {
    resetTask();
    setIsAddingTask(false);
  };

  /**
   * Gets current date and time in Hebrew locale format
   * @returns {string} Formatted date/time string (HH:MM DD/MM/YY)
   */
  const getCurrentFormattedDate = () => {
    const currentDate = new Date();

    // Get the time in "HH:MM" format
    const time = currentDate.toLocaleTimeString("he-IL", {
      hour: "2-digit",
      minute: "2-digit",
    });

    // Get the date in "DD/MM/YY" format
    const date = currentDate
      .toLocaleDateString("he-IL", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      })
      .replace(/\./g, "/");

    return `${time} ${date}`;
  };

  // Users need ObligoExceptionPage permission (not just ViewObligoData) to add/edit tasks
  const canAddTasks = user?.accessRights?.ObligoExceptionPage || user?.accessRights?.admin;

  return (
    <Box sx={{ width: "100%" }}>
      {error}
      {canAddTasks && (
        isAddingTask ? (
          <AddTaskForm
            newTask={newTask}
            setNewTask={setNewTask}
            onCancel={handleCancelAddTask}
            onSubmit={handleAddTask}
            loading={loading}
          />
        ) : (
          <Box
            sx={{ width: "100%", display: "flex", justifyContent: "flex-end" }}
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddTaskIcon sx={{ ml: 1 }} />}
              onClick={() => setIsAddingTask(true)}
              sx={{ mb: 2, mt: 2 }}
            >
              משימה חדשה
            </Button>
          </Box>
        )
      )}

      {updateTasks != "undefined" &&
        updateTasks?.map((note, index) => (
          <Accordion
            key={index}
            expanded={expanded === `panel${index}`}
            onChange={handleChange(`panel${index}`)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`panel${index}a-content`}
              id={`panel${index}a-header`}
              sx={{
                backgroundColor: "#f5f5f5",
                "& .MuiAccordionSummary-content": {
                  justifyContent: "space-between",
                },
              }}
            >
              <Typography variant="subtitle1">
                מספר משימה: {note.CUSTNOTE}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: note.CUSTNOTESTEXT_SUBFORM?.TEXT,
                  }}
                />
                {canAddTasks && note.STATDES !== "בוצעה" && (
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <TextField
                      fullWidth
                      multiline
                      variant="outlined"
                      size="small"
                      placeholder="הוסף הודעה חדשה..."
                      // value={newMessages[index] || ""}
                      value={newMessages[note.CUSTNOTE] || ""}
                      onChange={(e) =>
                        setNewMessages({
                          ...newMessages,
                          [note.CUSTNOTE]: e.target.value,
                        })
                      }
                      InputProps={{ sx: { direction: "rtl" } }}
                    />
                    <IconButton
                      color="primary"
                      onClick={() =>
                        addMessage(
                          ordname,
                          note.CUSTNOTE,
                          newMessages[note.CUSTNOTE],
                          (formattedMessage) => {
                            // Update local task state with appended message
                            setUpdateTasks((prevTasks) => {
                              return prevTasks.map((task, taskIndex) => {
                                if (task.CUSTNOTE === note.CUSTNOTE) {
                                  return {
                                    ...task,
                                    CUSTNOTESTEXT_SUBFORM: {
                                      TEXT: task.CUSTNOTESTEXT_SUBFORM?.TEXT + formattedMessage,
                                    },
                                  };
                                }
                                return task;
                              });
                            });

                            // Update parent orders state with appended message
                            setOrders((prev) => {
                              return prev.map((order) => {
                                if (order.ORDNAME === ordname) {
                                  return {
                                    ...order,
                                    GENCUSTNOTES_SUBFORM: order.GENCUSTNOTES_SUBFORM.map((task) => {
                                      if (task.CUSTNOTE === note.CUSTNOTE) {
                                        return {
                                          ...task,
                                          CUSTNOTESTEXT_SUBFORM: {
                                            TEXT: task.CUSTNOTESTEXT_SUBFORM?.TEXT + formattedMessage,
                                          },
                                        };
                                      }
                                      return task;
                                    }),
                                  };
                                }
                                return order;
                              });
                            });

                            // Clear input field after successful append
                            setNewMessages({
                              ...newMessages,
                              [note.CUSTNOTE]: "",
                            });
                          }
                        )
                      }
                      disabled={!newMessages[note.CUSTNOTE]?.trim()}
                      sx={{ mt: 1 }}
                    >
                      <AddCircleOutlineIcon />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
    </Box>
  );
};